import { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/ui/select";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";
import { Category } from "@/features/categories/service/categoryService";
import { Badge } from "@/shared/ui/badge";
import { useTranslation } from "@/shared/i18n";

interface CategoryOrderModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    categories: Category[];
    onSave: (orderedCategories: Category[], removedCategoryIds: string[]) => Promise<void>;
    isSaving?: boolean;
}

interface SortableCategoryItemProps {
    category: Category;
    index: number;
    onRemove: () => void;
}

const SortableCategoryItem = ({ category, index, onRemove }: SortableCategoryItemProps) => {
    const { t } = useTranslation('categories');
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: category.id! });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="flex items-center gap-4 p-4 bg-[#333333] border border-white/10 rounded-lg"
        >
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 text-white/40 hover:text-white/60"
            >
                <GripVertical className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
                <span className="text-white font-medium">{category.name}</span>
            </div>
            <span className="text-sm text-white/60">{t('orderLabel', { order: index + 1 })}</span>
            <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onRemove}
                className="text-destructive hover:text-destructive"
            >
                <X className="h-4 w-4" />
            </Button>
        </div>
    );
};

export const CategoryOrderModal = ({
    open,
    onOpenChange,
    categories,
    onSave,
    isSaving = false,
}: CategoryOrderModalProps) => {
    const { t } = useTranslation('categories');
    const [orderedCategories, setOrderedCategories] = useState<Category[]>([]);
    const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Inicializa com categorias que já têm order preenchido
    // Filtra apenas categorias do tipo PRESET
    useEffect(() => {
        if (open && categories.length > 0) {
            const presetCategories = categories.filter((cat) => cat.type === "PRESET");
            
            const withOrder = presetCategories
                .filter((cat) => cat.order !== null && cat.order !== undefined)
                .sort((a, b) => (a.order || 0) - (b.order || 0));
            
            const withoutOrder = presetCategories.filter(
                (cat) => cat.order === null || cat.order === undefined
            );

            setOrderedCategories(withOrder);
            setAvailableCategories(withoutOrder);
            setSelectedCategoryId(""); // Reset select ao abrir modal
        }
    }, [open, categories]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = orderedCategories.findIndex((cat) => cat.id === active.id);
            const newIndex = orderedCategories.findIndex((cat) => cat.id === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrdered = [...orderedCategories];
                const [removed] = newOrdered.splice(oldIndex, 1);
                newOrdered.splice(newIndex, 0, removed);
                setOrderedCategories(newOrdered);
            }
        }
    };

    const handleAddCategory = (category: Category) => {
        setOrderedCategories([...orderedCategories, category]);
        setAvailableCategories(availableCategories.filter((cat) => cat.id !== category.id));
        setSelectedCategoryId(""); // Reset select após adicionar
    };

    const handleRemoveCategory = (category: Category) => {
        setOrderedCategories(orderedCategories.filter((cat) => cat.id !== category.id));
        setAvailableCategories([...availableCategories, category]);
    };

    const handleSave = async () => {
        // Identifica categorias que foram removidas (tinham order antes, mas não estão mais na lista)
        // Considera apenas categorias PRESET
        const presetCategories = categories.filter((cat) => cat.type === "PRESET");
        const originalOrderedIds = presetCategories
            .filter((cat) => cat.order !== null && cat.order !== undefined)
            .map((cat) => cat.id!);
        
        const currentOrderedIds = orderedCategories.map((cat) => cat.id!);
        const removedCategoryIds = originalOrderedIds.filter(
            (id) => !currentOrderedIds.includes(id)
        );

        await onSave(orderedCategories, removedCategoryIds);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#1A1A1A] border-[#333333] max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-white">{t('orderCategoriesTitle')}</DialogTitle>
                    <DialogDescription className="text-gray-400">
                        {t('orderCategoriesDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-4">
                    {/* Categorias Disponíveis - Select no topo */}
                    {availableCategories.length > 0 && (
                        <div className="mx-1">
                            <h3 className="text-sm font-medium text-white mb-3">
                                {t('addCategory')}
                            </h3>
                            <Select
                                value={selectedCategoryId}
                                onValueChange={(value) => {
                                    setSelectedCategoryId(value);
                                    const category = availableCategories.find(
                                        (cat) => cat.id === value
                                    );
                                    if (category) {
                                        handleAddCategory(category);
                                    }
                                }}
                            >
                                <SelectTrigger className="w-full bg-[#2A2A2A] border-white/5 text-white">
                                    <SelectValue placeholder={t('selectCategoryToAdd')} />
                                </SelectTrigger>
                                <SelectContent className="bg-[#2A2A2A] border-white/5">
                                    {availableCategories.map((category) => (
                                        <SelectItem
                                            key={category.id}
                                            value={category.id!}
                                            className="text-white focus:bg-[#333333]"
                                        >
                                            {category.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Categorias Ordenadas */}
                    <div>
                        <h3 className="text-sm font-medium text-white mb-3">
                            {t('orderedCategories')} ({orderedCategories.length})
                        </h3>
                        {orderedCategories.length === 0 ? (
                            <p className="text-sm text-white/60 py-4">
                                {t('noOrderedCategories')}
                            </p>
                        ) : (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={orderedCategories.map((cat) => cat.id!)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="space-y-2">
                                        {orderedCategories.map((category, index) => (
                                            <SortableCategoryItem
                                                key={category.id}
                                                category={category}
                                                index={index}
                                                onRemove={() => handleRemoveCategory(category)}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isSaving}
                        className="bg-[#333333] text-white border-[#444444] hover:bg-[#444444]"
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="bg-[#D91657] text-white hover:bg-[#C0144D]"
                    >
                        {isSaving ? t('saving') : t('saveOrdering')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

